import type { Wire } from '@/shared/schemas/wire.js'
import { Block } from '@ttab/elephant-api/newsdoc'
import { DEFAULT_TIMEZONE } from '../../src/defaults/defaultTimezone.js'
import { newLocalDate } from '@/shared/datetime.js'
import { UTCDate } from '@date-fns/utc'

/**
 * Create a template structure for an assignment
 * @returns Block
 */
export function assignmentPlanningTemplate({
  assignmentType,
  planningDate,
  slugLine,
  title,
  wires,
  assignmentData,
  assignee
}: {
  assignmentType: string
  planningDate: string
  slugLine?: string
  title?: string
  wires?: Wire[]
  assignmentData?: Block['data']
  assignee: { id: string, name: string } | null | undefined
}): Block {
  const systemNow = newLocalDate(DEFAULT_TIMEZONE)
  const systemHour = systemNow.getHours().toString()

  const startDateAndTime = (type: string): string => {
    const isTextFlashOrEditorialInfo = ['text', 'flash', 'editorial-info'].includes(type)

    if (isTextFlashOrEditorialInfo) {
      const utcTime = new UTCDate().toISOString().split('T')[1]
      return new Date(`${planningDate}T${utcTime}`).toISOString()
    } else {
      return new Date(`${planningDate}T00:00:00`).toISOString()
    }
  }

  const wireContentSourceDocument: Block[] = (!wires?.length)
    ? []
    : wires.map((wire) =>
      Block.create({
        type: 'tt/wire',
        uuid: wire.id,
        title: wire.fields['document.title'].values[0],
        rel: 'source-document',
        data: {
          version: wire.fields['current_version'].values[0]
        }
      })
    )

  const author: Block[] = [
    assignee
      ? Block.create({
        uuid: assignee.id,
        type: 'core/author',
        title: assignee.name,
        rel: 'assignee',
        role: 'primary'
      })
      : undefined
  ].filter((x): x is Block => x !== undefined)

  // Text and editorial-info assignments should be set with a publish_slot
  if (assignmentType === 'text' || assignmentType === 'editorial-info') {
    if (assignmentData && !assignmentData.publish_slot) {
      assignmentData.publish_slot = systemHour
    }
  }

  // Plain dates, are getting set with UI
  return Block.create({
    id: crypto.randomUUID(),
    type: 'core/assignment',
    title: title || undefined,
    // N.B. Use provided assignmentData or default values
    data: assignmentData || {
      full_day: 'false',
      end_date: planningDate,
      ...((assignmentType === 'text' || assignmentType === 'editorial-info') && { publish_slot: systemHour }),
      start_date: planningDate,
      start: startDateAndTime(assignmentType),
      // Flash and timeless assignments are persisted but not part of the
      // planning's published artifact.
      public: assignmentType === 'flash' || assignmentType === 'timeless'
        ? 'false'
        : 'true'
    },
    meta: [
      {
        type: 'tt/slugline',
        value: slugLine || ''
      },
      {
        type: 'core/description',
        data: {
          text: ''
        },
        role: 'internal'
      },
      {
        type: 'core/assignment-type',
        value: assignmentType
      }
    ],
    links: [
      ...wireContentSourceDocument,
      ...author
    ]
  })
}
